import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { parseDateOnly } from '@/lib/drawHistory';

interface DrawDataStatusProps {
  loadedCount: number;
  analyzedCount: number;
  invalidCount: number;
  loading: boolean;
}

interface DrawDataStatusInfo {
  total_count: number | null;
  min_round: number | null;
  latest_round: number | null;
  latest_draw_date: string | null;
  missing_round_count: number | null;
  last_successful_sync_at: string | null;
  last_cron_sync_at: string | null;
  last_cron_status: string | null;
}

// 일반 오류 메시지(공개 UI 노출용). raw RPC 오류 상세는 공개하지 않는다.
const STATUS_ERROR = '데이터 현황을 불러오지 못했습니다.';

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
}

/**
 * YYYY-MM-DD(날짜만) 표기는 new Date(dateOnly)에 의존하지 않아
 * 브라우저 타임존에 따라 전날로 표시되는 문제를 방지한다.
 */
function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const parsed = parseDateOnly(dateStr);
  if (parsed) return `${parsed.year}년 ${parsed.month}월 ${parsed.day}일`;
  return dateStr;
}

// cron(스케줄) 동기화 상태 라벨. 이력이 없으면 '-'(미설정으로 단정하지 않음).
function cronStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'success':
      return '성공';
    case 'partial':
      return '일부 성공';
    case 'failed':
      return '실패';
    case 'running':
      return '진행 중';
    default:
      return '-';
  }
}

/**
 * 데이터 최신성·완전성 상태 표시.
 * public-safe RPC(get_draw_data_status)의 집계 결과만 읽고, draw_sync_log 원본은 노출하지 않는다.
 * - DB total_count는 loadedCount(실제 로드 건수)와 비교
 * - analyzedCount(기간/고급 필터 적용 후 분석 대상)는 화면 표시용으로만 사용
 * - loading 중에는 count mismatch를 판정하지 않음
 * - incomplete(비정상 행)/누락 회차/DB total-로드 불일치를 각각 구분해서 표시
 * - RPC 실패 시 일반 오류 메시지 + "다시 시도" 제공(중복 클릭 방지, stale/unmount 응답 무시)
 */
export default function DrawDataStatus({
  loadedCount,
  analyzedCount,
  invalidCount,
  loading,
}: DrawDataStatusProps) {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<DrawDataStatusInfo | null>(null);
  const [statusError, setStatusError] = useState('');
  const [statusLoading, setStatusLoading] = useState(true);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // 재사용 가능한 상태 로드 함수: 로딩/오류/성공 상태를 명확히 구분
  const loadStatus = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    abortRef.current?.abort(); // 이전 진행 중 요청 실제 취소
    const controller = new AbortController();
    abortRef.current = controller;

    setStatusLoading(true);
    setStatusError(''); // 재시도 시작 시 이전 오류 초기화

    try {
      const { data, error } = await supabase
        .rpc('get_draw_data_status')
        .abortSignal(controller.signal);

      // stale 응답 / abort 무시
      if (requestId !== requestIdRef.current || controller.signal.aborted) return;

      if (error) {
        // raw DB 오류는 공개하지 않고 개발 로그로만 남김
        if (import.meta.env.DEV) {
          console.error('[DrawDataStatus] 상태 RPC 실패:', error.message);
        }
        setStatusError(STATUS_ERROR);
        return;
      }
      setStatus(data as DrawDataStatusInfo);
    } catch (err) {
      if (requestId !== requestIdRef.current || controller.signal.aborted) return;
      if (import.meta.env.DEV) {
        console.error('[DrawDataStatus] 상태 RPC 예외:', err);
      }
      setStatusError(STATUS_ERROR);
    } finally {
      // unmount/재시도로 abort된 요청은 loading을 건드리지 않는다
      if (requestId === requestIdRef.current && !controller.signal.aborted) {
        setStatusLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => {
      // unmount 시 현재 요청을 명확히 무효화 + 실제 취소
      requestIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, [loadStatus]);

  const missingCount = status?.missing_round_count ?? 0;
  // DB total과 loadedCount 비교 — loading 중에는 판정하지 않음
  const loadMismatch =
    !loading &&
    status != null &&
    status.total_count != null &&
    loadedCount !== status.total_count;

  // 서로 다른 완전성 경고를 각각 구분해서 수집
  const warnings: string[] = [];
  if (loadMismatch) {
    warnings.push(`DB 전체(${status?.total_count}회)와 로드된 회차(${loadedCount}회)가 일치하지 않습니다.`);
  }
  if (missingCount > 0) {
    warnings.push(`${missingCount}개 회차가 누락되어 있습니다.`);
  }
  if (invalidCount > 0) {
    warnings.push(`비정상 데이터 ${invalidCount}건이 통계에서 제외되었습니다.`);
  }

  // 마지막 성공 동기화 이후 장기간 경과(8일 초과 → 주간 추첨 1회 이상 지연)
  const lastSuccessAt = status?.last_successful_sync_at;
  let staleSyncDays = 0;
  if (lastSuccessAt) {
    const diffMs = Date.now() - new Date(lastSuccessAt).getTime();
    if (Number.isFinite(diffMs)) staleSyncDays = Math.floor(diffMs / 86400000);
  }
  if (staleSyncDays > 8) {
    warnings.push(`마지막 성공 동기화 후 ${staleSyncDays}일이 경과했습니다.`);
  }

  const hasWarning = warnings.length > 0;

  return (
    <div className="w-full rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="w-6 h-6 rounded-lg bg-secondary-100 flex items-center justify-center">
          <i className="ri-database-2-line text-secondary-700 text-sm"></i>
        </div>
        <h4 className="font-heading text-sm text-foreground-800">데이터 현황</h4>
        {statusLoading && !status && (
          <span className="inline-flex items-center gap-1 text-foreground-400 text-[11px]">
            <i className="ri-loader-4-line animate-spin"></i>
            불러오는 중
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-400 text-[11px]">
          <i className="ri-global-line text-[11px]"></i>
          데이터 출처: 동행복권
        </span>
        {isAdmin && (
          <a
            href="/admin"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE('/admin');
            }}
            className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            재동기화 관리
          </a>
        )}
      </div>

      {statusError ? (
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs text-foreground-400">{statusError}</p>
          <button
            onClick={loadStatus}
            disabled={statusLoading}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-refresh-line"></i>
            {statusLoading ? '다시 시도 중...' : '다시 시도'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <p className="text-[11px] text-foreground-400 mb-0.5">DB 최신 회차</p>
            <p className="text-sm font-medium text-foreground-800">
              {status?.latest_round != null ? `${status.latest_round}회` : '-'}
            </p>
            <p className="text-[11px] text-foreground-400 mt-0.5">
              {formatDateOnly(status?.latest_draw_date)}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-400 mb-0.5">마지막 동기화</p>
            <p className="text-sm font-medium text-foreground-800">
              {status?.last_successful_sync_at ? formatTimestamp(status.last_successful_sync_at) : '-'}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-400 mb-0.5">분석 대상 회차</p>
            <p className="text-sm font-medium text-foreground-800">
              {loading ? '...' : `${analyzedCount.toLocaleString()}회`}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-foreground-400 mb-0.5">누락 회차</p>
            <p className={`text-sm font-medium ${missingCount > 0 ? 'text-accent-600' : 'text-foreground-800'}`}>
              {missingCount > 0 ? `${missingCount}회` : '없음'}
            </p>
          </div>
        </div>
      )}

      {/* 스케줄(cron) 동기화 상태 — 관리자 전용 정보. 이력이 없으면 "미설정"으로 단정하지 않음 */}
      {isAdmin && (
        <div className="mt-3 pt-3 border-t border-background-100 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-foreground-400 flex items-center gap-1">
            <i className="ri-time-line text-[11px]"></i>
            스케줄(cron) 동기화
          </span>
          {status?.last_cron_sync_at ? (
            <>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                status.last_cron_status === 'success'
                  ? 'bg-secondary-100 text-secondary-700'
                  : status.last_cron_status === 'partial' || status.last_cron_status === 'failed'
                  ? 'bg-accent-100 text-accent-700'
                  : status.last_cron_status === 'running'
                  ? 'bg-background-100 text-foreground-500'
                  : 'bg-background-100 text-foreground-500'
              }`}>
                {cronStatusLabel(status.last_cron_status)}
              </span>
              <span className="text-[11px] text-foreground-400">{formatTimestamp(status.last_cron_sync_at)}</span>
            </>
          ) : (
            <span className="text-[11px] text-foreground-400">이력 없음</span>
          )}
        </div>
      )}

      {hasWarning && (
        <div className="mt-3 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
          <div className="text-accent-700 text-xs space-y-1">
            {warnings.map((w, i) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}